import { FC } from 'react';

import { PlaceholderBar } from '../../components/loading-skeleton/index';

type StakeholdersPlaceholderProps = {
  rows?: number;
};

// Table-only skeleton — shown in the list slot while a sort/filter/search/tab reload runs,
// so the tabs + toolbar stay put instead of flashing the whole-page skeleton.
export const StakeholdersTablePlaceholder: FC<StakeholdersPlaceholderProps> = ({ rows = 6 }) => (
  <div className="w-full animate-pulse overflow-x-auto">
    <div className="w-full rounded-md lg:shadow-sm">
      <table className="h-fit w-full divide-y-[1px] divide-[#F2F2F2] border-b-[1px]">
        <thead>
          <tr className="h-11 bg-gray-50">
            <td className="w-10 pl-4">
              <PlaceholderBar className="size-4 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-12 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
            </td>
            <td className="pl-4">
              <PlaceholderBar className="h-4 w-14 rounded bg-gray-100" />
            </td>
            <td className="w-12" />
          </tr>
        </thead>
        <tbody className="divide-y-[1px] divide-[#F2F2F2] bg-white">
          {Array.from({ length: rows }).map((_, index) => (
            <tr className="h-16" key={index}>
              <td className="w-10 pl-4">
                <PlaceholderBar className="size-4 rounded bg-gray-100" />
              </td>
              <td className="p-4">
                <div className="flex items-center gap-3">
                  <PlaceholderBar className="size-9 shrink-0 rounded-full bg-gray-100" />
                  <PlaceholderBar className="h-4 w-32 rounded bg-gray-100" />
                </div>
              </td>
              <td className="pl-4">
                <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
              </td>
              <td className="pl-4">
                <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
              </td>
              <td className="pl-4">
                <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
              </td>
              <td className="pl-4">
                <PlaceholderBar className="h-4 w-12 rounded bg-gray-100" />
              </td>
              <td className="pl-4">
                <PlaceholderBar className="h-4 w-8 rounded bg-gray-100" />
              </td>
              <td className="pl-4">
                <PlaceholderBar className="h-6 w-16 rounded bg-gray-100" />
              </td>
              <td className="w-12 pr-4">
                <PlaceholderBar className="size-5 rounded bg-gray-100" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const StakeholdersPlaceholder: FC<StakeholdersPlaceholderProps> = ({ rows = 6 }) => (
  <div className="flex h-fit min-h-[200px] w-full animate-pulse flex-col gap-4 overflow-hidden rounded-md lg:p-4 lg:shadow-sm">
    {/* Stakeholders / Terminated tabs */}
    <div className="flex h-[38px] w-full gap-4 border-b border-gray-200">
      <PlaceholderBar className="h-5 w-28 rounded bg-gray-100" />
      <PlaceholderBar className="h-5 w-24 rounded bg-gray-100" />
    </div>

    {/* Title + toolbar (search, filters, bulk modify, export, import, add) */}
    <div className="flex w-full items-center justify-between gap-4 lg:px-4">
      <PlaceholderBar className="h-5 w-40 rounded bg-gray-100" />
      <div className="flex items-center gap-2">
        <PlaceholderBar className="size-9 rounded bg-gray-100" />
        <PlaceholderBar className="h-9 w-16 rounded bg-gray-100" />
        <PlaceholderBar className="h-9 w-24 rounded bg-gray-100" />
        <PlaceholderBar className="h-9 w-28 rounded bg-gray-100" />
        <PlaceholderBar className="h-9 w-20 rounded bg-gray-100" />
        <PlaceholderBar className="h-9 w-20 rounded bg-gray-100" />
        <PlaceholderBar className="h-9 w-32 rounded bg-gray-100" />
      </div>
    </div>

    <StakeholdersTablePlaceholder rows={rows} />
  </div>
);
